import React, { useState } from "react";
import { CheckCircle2, XCircle, AlertTriangle, BookOpen, Info } from "lucide-react";

/**
 * Transcripción de la doc oficial "PP Club API Doc 20260831" (PDF de PPPoker).
 * Copia completa en /app/memory/API_PPPOKER.md §11 y §12.
 */

const SI = [
  ["Cargar fichas a un jugador, miembro o agente de un club",
    "POST club/user/chips · tope 10.000.000 PP por operación · transaction_id propio (idempotente) · operator_id para que quede a nombre del cajero"],
  ["Consultar el saldo de fichas de un jugador", "GET club/user/chips · por club_id + uid"],
  ["Ver el historial de envíos y recepciones de fichas del club", "GET club/chips/flow · rango máx 14 días, dentro de los últimos 90"],
  ["Datos de juego de un jugador: resultado, manos, rake, MTT y SpinUp", "GET host/club/member/detail · rango máx 31 días, histórico ~3 meses, el «hasta» no puede ser futuro"],
  ["Cuota (rake) y Entrada de SpinUp de un jugador", "GET host/club/member/detail · campos fee y spinup_buyin — los mismos nombres que usa la app"],
  ["Resultado, topes, suspensión y auto-ban de cada club de la unión", "GET host/league/stats · con desglose por tipo de juego"],
  ["Resultado de un agente: semana, día, semana pasada y ayer + sus topes", "GET host/agent/stats · hay que conocer el uid del agente"],
  ["Datos por miembro de PPSR y de PPST, ya convertidos por tasa", "GET host/ppsr/stats y host/ppst/stats"],
  ["Ligas que componen la superliga", "GET host/super-league/stats"],
  ["uids de los miembros certificados del club", "GET club/certified/members"],
];

const NO = [
  ["Cargar fichas a la CAJA de un agente (su «Saldo de crédito»)",
    "El POST sólo tiene uid, club_id, amount, transaction_id y operator_id: no hay ningún parámetro para elegir el destino. Siempre carga a las fichas personales del usuario. Probado: un agente con «Saldo de crédito» 1.702,38 en la app tenía 0,00 en club/user/chips — son dos bolsas distintas."],
  ["Cuota / rake y Entrada de SpinUp de un AGENTE (su equipo)",
    "Existen sólo por jugador (fee y spinup_buyin en host/club/member/detail). agent/stats devuelve 49 campos y ninguno es de rake. Como la API tampoco lista el equipo del agente, no se pueden sumar."],
  ["Saldo de crédito, Fichas de miembro invitado y Fichas en el juego",
    "La tarjeta «Datos del agente» de la app los muestra, pero no están en ningún endpoint. club/user/chips sólo da el saldo de fichas del jugador."],
  ["Porcentaje del RC y Retorno de cuota", "No existen en la API. Salen de la planilla que importa Agentes United."],
  ["Descargar fichas (retirar) de forma documentada",
    "La doc habla de «Send/Receive» pero no explica el retiro. La hipótesis es amount negativo: no se prueba sin que PPPoker lo confirme."],
  ["Listar todos los miembros de un club", "Sólo existe la lista de «certificados», que no es lo mismo."],
  ["Listar los agentes de un club o el equipo de un agente", "Para consultar un agente hay que saber su uid de antemano."],
  ["Listar los clubes de la unión por separado", "Sólo aparecen dentro de host/league/stats."],
  ["Crear o administrar mesas", "No existe en la API."],
  ["Aceptar solicitudes de miembros o crear agentes", "No existe en la API."],
  ["Cambiar rake o configuración del club", "No existe en la API."],
  ["Recibir webhooks de PPPoker", "La doc no menciona ninguno: la confirmación llega en la misma respuesta. El receptor del CRM queda listo por si los agregan."],
  ["Ver Blackjack como categoría propia", "Las categorías son EV Chop, Jackpot, Lucky Draw, Color Game, Russian Poker, Crash Game y «otros». Blackjack cae en «otros» o no se mide."],
];

const DEPENDE = [
  ["Alcance a toda la Unión 2006",
    "Consultando con HALCÓN V.I.P, la API devolvió un solo club. Hay que probar con EVOLUTION (1336011) whitelisteado para saber si el host de la unión ve todos."],
  ["Whitelist", "Es por IP + club. Cada club nuevo hay que pedirlo a PPPoker."],
  ["Producción", "pptest es vendor de prueba. Falta el vendor y el endpoint definitivos."],
  ["Rate limit", "Existe (error 40004) pero el número exacto no está documentado."],
];

const ENDPOINTS = [
  ["POST", "/club/user/chips", "Transferencia de fichas (el único que mueve plata)", "uid, club_id, amount (×100), transaction_id, operator_id?"],
  ["GET", "/club/user/chips", "Saldo de un jugador", "club_id, uid"],
  ["GET", "/club/chips/flow", "Historial de fichas del club", "club_id, start?, end? (unix, máx 14 días)"],
  ["GET", "/club/certified/members", "uids de miembros certificados", "club_id"],
  ["GET", "/host/club/member/detail", "Datos de juego de un miembro", "club_id, uid, start_day, end_day, utc_offset"],
  ["GET", "/host/league/stats", "Clubes de la liga (unión): resultado, topes, suspensión", "club_id"],
  ["GET", "/host/super-league/stats", "Ligas de la superliga", "club_id"],
  ["GET", "/host/ppsr/stats", "Miembros del PPSR (convertido por tasa)", "club_id"],
  ["GET", "/host/ppst/stats", "Miembros del PPST (agregado semanal/diario)", "club_id"],
  ["GET", "/host/agent/stats", "Agente: resultado, topes, suspensión, autoban", "club_id, agent_uid, utc_offset?"],
];

const ERRORES = [
  ["10000", "Parámetros inválidos"],
  ["10002", "Rango de fechas fuera de lo permitido"],
  ["10003", "El club no pertenece a esa organización (liga / superliga / PPSR / PPST)"],
  ["10004", "El club fue disuelto"],
  ["40000", "Sin permiso: IP o club fuera de la whitelist"],
  ["40001", "Saldo insuficiente"],
  ["40003", "Transacción duplicada (base de la idempotencia)"],
  ["40004", "Demasiadas requests (rate limit)"],
  ["40005", "Transacción fallida"],
  ["40006", "El club está suspendido de enviar fichas"],
  ["40007", "Usuario no encontrado o no es miembro del club"],
  ["40008", "Usuario baneado"],
  ["40009", "Agente no encontrado"],
  ["50000", "Error de sistema — hay que verificar el resultado final de la operación antes de reintentar"],
];

const Lista = ({ items, Icon, color, testid }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 divide-y dark:divide-gray-700" data-testid={testid}>
    {items.map(([t, d], i) => (
      <div key={i} className="p-3 flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${color}`} />
        <div>
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{t}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d}</p>
        </div>
      </div>
    ))}
  </div>
);

const Seccion = ({ titulo, children }) => (
  <div className="space-y-2">
    <h2 className="text-sm font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">{titulo}</h2>
    {children}
  </div>
);

export default function ApiPPPokerDoc() {
  const [ver, setVer] = useState("posibilidades");

  return (
    <div className="space-y-4" data-testid="api-pppoker-doc">
      <div className="rounded-lg border border-violet-300 dark:border-violet-800 bg-violet-50 dark:bg-violet-900/20 p-4">
        <p className="text-sm font-semibold text-violet-800 dark:text-violet-300 flex items-center gap-2">
          <BookOpen className="w-4 h-4" /> Qué se puede hacer con la API oficial de PPPoker
        </p>
        <p className="text-sm text-violet-800/80 dark:text-violet-200/80 mt-1">
          Transcripción de la documentación «PP Club API Doc 20260831». Son <b>10 endpoints</b>:
          uno escribe (transferencia de fichas) y nueve leen. Todos los importes viajan multiplicados
          por 100 — en este módulo se muestran ya en <b>PP chips</b>. Auth: un solo header con el
          vendor code, y el control real es la <b>whitelist de IP + club</b>.
        </p>
      </div>

      <div className="flex gap-1 flex-wrap">
        {[["posibilidades", "Posibilidades"], ["endpoints", "Endpoints"], ["errores", "Errores"], ["reglas", "Reglas y límites"]].map(([k, l]) => (
          <button key={k} onClick={() => setVer(k)} data-testid={`doc-sub-${k}`}
            className={`px-3 py-1 text-xs font-semibold rounded-full border ${ver === k
              ? "bg-violet-600 border-violet-600 text-white"
              : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>
            {l}
          </button>
        ))}
      </div>

      {ver === "posibilidades" && (
        <div className="space-y-5">
          <Seccion titulo={`Se puede (${SI.length})`}>
            <Lista items={SI} Icon={CheckCircle2} color="text-emerald-500" testid="doc-si" />
          </Seccion>
          <Seccion titulo={`No se puede (${NO.length})`}>
            <Lista items={NO} Icon={XCircle} color="text-rose-500" testid="doc-no" />
          </Seccion>
          <Seccion titulo={`Depende de PPPoker (${DEPENDE.length})`}>
            <Lista items={DEPENDE} Icon={AlertTriangle} color="text-amber-500" testid="doc-depende" />
          </Seccion>
        </div>
      )}

      {ver === "endpoints" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-x-auto" data-testid="doc-endpoints">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Método</th><th className="px-3 py-2 text-left">Path</th>
                <th className="px-3 py-2 text-left">Qué hace</th><th className="px-3 py-2 text-left">Parámetros</th>
              </tr>
            </thead>
            <tbody>
              {ENDPOINTS.map(([m, p, q, par], i) => (
                <tr key={i} className="border-t dark:border-gray-700">
                  <td className="px-3 py-1.5">
                    <span className={`px-1.5 py-0.5 rounded font-mono font-semibold ${m === "POST"
                      ? "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                      : "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"}`}>{m}</span>
                  </td>
                  <td className="px-3 py-1.5 font-mono whitespace-nowrap">/poker-api/vendor{p}</td>
                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{q}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-500">{par}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="px-3 py-2 text-xs text-gray-500 border-t dark:border-gray-700">
            Base: <span className="font-mono">https://www.pppoker.club</span> · header
            <span className="font-mono"> ven: &lt;vendor code&gt;</span> · POST y PUT sólo aceptan JSON.
          </p>
        </div>
      )}

      {ver === "errores" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden" data-testid="doc-errores">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr><th className="px-3 py-2 text-left w-24">Código</th><th className="px-3 py-2 text-left">Significado</th></tr>
            </thead>
            <tbody>
              {ERRORES.map(([c, d]) => (
                <tr key={c} className="border-t dark:border-gray-700">
                  <td className="px-3 py-1.5 font-mono font-semibold">{c}</td>
                  <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{d}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {ver === "reglas" && (
        <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-4 space-y-3" data-testid="doc-reglas">
          {[
            ["Importes ×100", "100 = 1 PP chip, en request y en response. En este módulo se convierte solo: si querés cargar 500 PP, pones 500."],
            ["Tope por operación", "1.000.000.000 en unidades de la API = 10.000.000 PP chips por request."],
            ["transaction_id", "Sólo letras y números, máximo 50 caracteres. Repetirlo devuelve 40003 → nunca se carga dos veces."],
            ["operator_id", "Opcional. Si va, la operación queda a nombre de ese operador; si no, a nombre del host del club."],
            ["Alianzas", "Si el club pertenece a una alianza, la transferencia se hace a través del club de la alianza (igual que en la app)."],
            ["chips/flow", "Rango máximo 14 días y el final tiene que estar dentro de los últimos 90 días."],
            ["member/detail", "Rango máximo 31 días, no futuro, y no antes de 3 meses previos al día 1 del mes actual. utc_offset obligatorio."],
            ["agent/stats", "El uid tiene que ser agente o superagente del club. Un subagente figura suspendido si su superagente lo está."],
            ["trace_id", "Cada respuesta trae uno. Es el dato que piden los ingenieros de PPPoker para investigar: el CRM lo guarda en todas las llamadas."],
            ["Error 50000", "No se reintenta a ciegas: primero hay que verificar el resultado final de esa transacción."],
          ].map(([t, d]) => (
            <div key={t} className="flex items-start gap-2">
              <Info className="w-4 h-4 mt-0.5 shrink-0 text-violet-500" />
              <p className="text-sm text-gray-700 dark:text-gray-300">
                <b className="text-gray-900 dark:text-gray-100">{t}:</b> {d}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
